import Form, { FieldWatch } from "@imminently/decisively-ui/Form";

import { AttributeIcon, getGraphNodeTypeColor, getNodeType, GraphNodeType } from "@/common";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { Pane, SplitPane } from "@/common/SplitPane";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { RELEASE_LAYER } from "@/ui/dialogs/layers";
import { useConfirmDialog } from "@imminently/decisively-ui/ConfirmDialog";
import { GraphNode, ValueType } from "@packages/commons";
import Button from "@imminently/decisively-ui/Button";
import Tabs from "@imminently/decisively-ui/Tabs";
import Dialog, { useDialog } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";

import { AttributeInfoFormValues, useAttributeForm } from "./useAttributeForm";
import { AttributeForm } from "./AttributeForm";
import { AttributeMetadata } from "./AttributeMetadata";
import { AttributeExplanation } from "./AttributeExplanation";
import { AttributeUsage } from "./DocumentUsage";
import { AttributeGraph } from "./AttributeGraph";
import { ProjectTechnicalWriterGuard } from "@/auth/ComponentGuard";
import { AttributeIDField } from "./AttributeIDField";
import { AttributeTriggers } from "./AttributeTriggers";
import { useTranslation } from "react-i18next";
import { hexToHSL } from "@/theme";
import { Tooltip, Typography } from "@mui/material";
import capitalize from "lodash/capitalize";
import { useCallback } from "react";


/**
 * A basic preview version of the Attribute component, without the link to the edit modal.
 * It uses form values to get live updates of the attribute being edited.
 * This makes quickly seeing what the attribtue is, way easier.
 */
export const AttributePreview = ({ node: _node, formValues }: { node: GraphNode | null; formValues?: Partial<AttributeInfoFormValues> }) => {
  const node = formValues ? ({ ..._node, ...formValues } as GraphNode) : _node;
  let nodeType = getNodeType(node as unknown as GraphNode);

  const color = getGraphNodeTypeColor(nodeType as any);

  const darkColor = hexToHSL(color, 80);

  if (!node) return null;

  return (
    <div
      className="flex gap-2 py-1 px-3 rounded-md items-center justify-start border border-border"
      style={{ backgroundColor: color }}
    >
      <Tooltip
        title={node.type}
        placement="right"
      >
        <div
          className="flex items-center justify-center rounded-full border-2 border-background h-8 w-8 [&_svg]:w-5 [&_svg]:h-5"
          style={{ backgroundColor: darkColor }}
        >
          <AttributeIcon type={node.type as ValueType} nodeType={nodeType} />
        </div>
      </Tooltip>
      <Typography noWrap className="flex-1 italic text-sm">
        {capitalize(node.entity)}
      </Typography>
      <div className="flex items-center">
        <Typography className="px-2">
          {capitalize(nodeType)}
        </Typography>
      </div>
    </div>
  );
};

const AttributeEditModalContent = ({ node, close }: { node: GraphNode; close: () => void }) => {
  const { query, defaultValues, submit } = useAttributeForm(node);

  const release = useFullReleaseStrict();

  const isLoading = query.query.isLoading;

  return (
    <Form<AttributeInfoFormValues>
      className={"flex flex-col h-[calc(100%-0.5rem)] overflow-hidden"}
      defaultValues={defaultValues}
      onSubmit={async (values) => {
        try {
          await submit(values);
          close();
        } catch (e) {
          // error notification should have been auto handled
        }
      }}
    >
        <SplitPane className={"h-full overflow-hidden"}>
          <Pane>
            <div className="flex flex-col h-full overflow-hidden">
              <Tabs defaultValue="system" className="flex flex-col h-full flex-1">
                <Tabs.List className="px-4 pt-4">
                  <Tabs.Trigger value="system">System</Tabs.Trigger>
                  <Tabs.Trigger value="interview">Interview</Tabs.Trigger>
                  <Tabs.Trigger value="triggers">Triggers</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="system" className="flex-1 overflow-y-auto p-3 mb-16">
                  <AttributeForm />
                </Tabs.Content>
                <Tabs.Content value="interview" className="flex-1 overflow-y-auto p-3 mb-16">
                  <AttributeMetadata />
                </Tabs.Content>
                <Tabs.Content value="triggers" className="flex-1 overflow-y-auto p-3 mb-16">
                  <AttributeTriggers />
                </Tabs.Content>
              </Tabs>
              <ProjectTechnicalWriterGuard>
                <div className="sticky bottom-0 bg-background p-4 z-10">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    loading={isLoading}

                   label="Save">
                    Save
                  </Button>
                </div>
              </ProjectTechnicalWriterGuard>
            </div>
          </Pane>
          <Pane>
            <div className="flex flex-col h-full overflow-hidden">
              <Tabs defaultValue="usage" className="flex flex-col h-full">
                <Tabs.List className="px-4 pt-4">
                  <Tabs.Trigger value="usage">Usage</Tabs.Trigger>
                  <Tabs.Trigger value="definition">Definition</Tabs.Trigger>
                  <Tabs.Trigger value="graph">Graph</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="usage" className="flex-1 overflow-y-auto p-3">
                  <div className="flex flex-col gap-4">
                    <FieldWatch names={Object.keys(defaultValues)}>
                      {(values) => (
                        <AttributePreview node={node} formValues={values as Partial<AttributeInfoFormValues>} />
                      )}
                    </FieldWatch>
                    {/* we want to display the value for ease of dev/debugging */}
                    <AttributeIDField id={node.id} />
                    <AttributeUsage showInterviews node={node} onClick={close} />
                  </div>
                </Tabs.Content>
                <Tabs.Content value="definition" className="flex-1 overflow-y-auto p-3">
                  <AttributeExplanation node={node} />
                </Tabs.Content>
                <Tabs.Content value="graph" className="flex-1 overflow-y-auto p-3">
                  <AttributeGraph attribute={node} release={release} />
                </Tabs.Content>
              </Tabs>
            </div>
          </Pane>
        </SplitPane>
    </Form>
  );
};

export const useEditAttributeModal = () => {
 const graph = useParsedGraph();
  const { t } = useTranslation();
  const { pushConfirmDialog } = useConfirmDialog();

  const { pushDialog } = useDialog();
  return useCallback((nodeId: string) => {
    const attribute = graph.node(nodeId);
    if (!attribute) {
      // Show error dialog if attribute not found
      pushConfirmDialog({
        layer: RELEASE_LAYER,
        title: "Error",
        description: t('edit_attr_modal_cant_find_attr_error'),
        confirmLabel: "OK",
        confirmIntent: "secondary",
        onConfirm: () => undefined,
      });
      return;
    }

    pushDialog({
      layer: RELEASE_LAYER,
      options: { intent: "cover" },
      component: ({ close }) => (
        <Dialog.Content className="!p-0 flex flex-col overflow-hidden">
          <DialogHeader title={attribute?.description || "Edit Attribute"} className="p-4 pb-0" controls={{ allowResize: true }} />
          <AttributeEditModalContent node={attribute} close={close} />
        </Dialog.Content>
      ),
    });
  }, [graph, pushConfirmDialog, pushDialog, t]);
};
